import { useEffect, useRef, useState } from "react"
import { loadAeroponicEcSampleContext, loadAeroponicEcProfileCandidates, loadAeroponicEcProfileCoherence } from "./virtualFarmerApi"
import { usePortalLanguage } from "./virtualFarmerI18n"
import AeroponicOriginalRecordEvidence from "./AeroponicOriginalRecordEvidence"

const TEXT = {
     de: {
          title: "EC-Aufnahmekontext", load: "Aufnahmekontext ansehen", retry: "Erneut ansehen",
          loading: "Aufnahmekontext wird geladen …",
          unavailable: "Für diese Aufnahme ist kein verlässlich zugeordneter Kontext verfügbar.",
          auth: "Bitte anmelden, um den privaten Aufnahmekontext anzusehen.",
          ready: "Bitte zuerst den Kontozugang vervollständigen.",
          note: "Nur gespeicherte Metadaten dieser Aufnahme. Eine Profilgeneration belegt weder physische Kalibrierung noch EC25; keine Regelwirkung.",
          tick: "Aufnahmebeginn · Geräte-Tick", clock: "Millisekunden-Gerätetick, keine UTC-Zeit.",
          unknown: "Profilstand bei Aufnahme unbekannt", empty: "Kein aktives gespeichertes Profil bei Aufnahme",
          declared: "Profilgeneration nur deklariert", generation: "Generation",
          profiles: "Gespeicherte Profilbytes zuordnen", profileTitle: "Originale Profilbeobachtungen",
          profileLoading: "Originale Profilbeobachtungen werden geprüft …",
          coherent: "Originale Profilbytes stimmen mit der deklarierten Generation überein",
          profileUnavailable: "Keine eindeutig zuordenbaren Originalprofilbytes verfügbar.",
          profileNote: "Nur Bytezuordnung dieser Aufnahme, kein Nachweis für Anwendung, physische Kalibrierung, EC25 oder vollständige Historie.",
     },
     it: {
          title: "Contesto acquisizione EC", load: "Vedi contesto acquisizione", retry: "Riprova",
          loading: "Caricamento del contesto …",
          unavailable: "Nessun contesto associato in modo verificabile a questa acquisizione.",
          auth: "Accedi per vedere il contesto privato dell’acquisizione.",
          ready: "Completa prima l’accesso all’account.",
          note: "Solo metadati salvati per questa acquisizione. Una generazione del profilo non prova una calibrazione fisica o EC25; nessun controllo automatico.",
          tick: "Inizio acquisizione · tick dispositivo", clock: "Tick dispositivo in millisecondi, non una data UTC.",
          unknown: "Profilo sconosciuto all’acquisizione", empty: "Nessun profilo memorizzato attivo all’acquisizione",
          declared: "Generazione del profilo solo dichiarata", generation: "Generazione",
          profiles: "Associa i byte del profilo salvato", profileTitle: "Osservazioni originali del profilo",
          profileLoading: "Verifica delle osservazioni originali del profilo …",
          coherent: "I byte originali corrispondono alla generazione dichiarata",
          profileUnavailable: "Byte originali del profilo non associabili in modo univoco.",
          profileNote: "Solo associazione dei byte di questa acquisizione, non prova di applicazione, calibrazione fisica, EC25 o cronologia completa.",
     },
     en: {
          title: "EC acquisition context", load: "View acquisition context", retry: "Try again",
          loading: "Loading acquisition context …",
          unavailable: "No reliably linked context is available for this acquisition.",
          auth: "Sign in to view the private acquisition context.",
          ready: "Complete account access first.",
          note: "Stored metadata for this acquisition only. A profile generation proves neither physical calibration nor EC25; no automatic control.",
          tick: "Acquisition start · device tick", clock: "Device tick in milliseconds, not a UTC timestamp.",
          unknown: "Profile state unknown at acquisition", empty: "No active stored profile at acquisition",
          declared: "Profile generation declared only", generation: "Generation",
          profiles: "Match stored profile bytes", profileTitle: "Original profile observations",
          profileLoading: "Checking original profile observations …",
          coherent: "Original profile bytes match the declared generation",
          profileUnavailable: "No uniquely associated original profile bytes are available.",
          profileNote: "Byte matching for this acquisition only, not evidence of application, physical calibration, EC25 or complete history.",
     },
}
const object = (value) => value !== null && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype
const keys = (value, expected) => object(value) && Object.keys(value).length === expected.length
     && expected.every((key) => Object.prototype.hasOwnProperty.call(value, key))
const id = (value) => Number.isSafeInteger(value) && value > 0
const decimalId = (value, prefix = "") => {
     if (typeof value !== "string" || !value.startsWith(prefix)) return false
     const digits = value.slice(prefix.length)
     return /^[1-9][0-9]{0,18}$/.exec(digits)?.[0] === digits && (digits.length < 19 || digits <= "9223372036854775807")
}
const uint32 = (value) => Number.isInteger(value) && value >= 0 && value <= 0xffffffff
const token = (value, minimum) => typeof value === "string" && value.length >= minimum && value.length <= 80 && /^[A-Za-z0-9_.:-]+$/.exec(value)?.[0] === value
const sha = (value) => typeof value === "string" && value.length === 64 && /^[0-9a-f]{64}$/.test(value)
const invalid = () => { throw new Error("Invalid EC context contract") }

// The server supplies account authority; Portal tenant ids are not Python
// account ids. This mapper verifies the selected sample, never current owners
// or a latest profile, and returns only metadata used by the view.
export function mapEcSampleContext(doc, sampleRowId, deviceId) {
     if (!id(sampleRowId) || !token(deviceId, 3)
          || !keys(doc, ["version", "visibility", "evidence_kind", "control_domain", "controls_hardware",
               "calibration_applied", "ec25_applied", "source", "captured_scope", "context", "evidence"])
          || doc.version !== 1 || doc.visibility !== "private" || doc.evidence_kind !== "raw_context"
          || doc.control_domain !== "aeroponic" || doc.controls_hardware !== false
          || doc.calibration_applied !== false || doc.ec25_applied !== false) invalid()
     const source = doc.source, scope = doc.captured_scope, context = doc.context
     if (!keys(source, ["record_id", "sample_row_id", "cycle_row_id", "cycle_id", "device_id",
          "producer_boot_id", "sequence", "content_sha256"])
          || !id(source.record_id) || source.sample_row_id !== sampleRowId || !id(source.sample_row_id)
          || !id(source.cycle_row_id) || !uint32(source.cycle_id) || source.device_id !== deviceId
          || !token(source.producer_boot_id, 8) || !uint32(source.sequence) || !sha(source.content_sha256)
          || !keys(scope, ["account_id", "account_run_id", "deployment_id", "node_id", "legacy_run_id"])
          || ![scope.account_id, scope.node_id, scope.legacy_run_id].every(id)
          || !decimalId(scope.account_run_id, "account-run:") || !decimalId(scope.deployment_id)
          || !keys(context, ["controller_uid", "started_ms", "clock_basis", "utc_mapped", "channels", "payload_sha256"])
          || typeof context.controller_uid !== "string" || context.controller_uid.length !== 24 || !/^[0-9A-F]{24}$/.test(context.controller_uid)
          || !uint32(context.started_ms) || context.clock_basis !== "monotonic_tick" || context.utc_mapped !== false
          || !sha(context.payload_sha256) || !Array.isArray(context.channels) || context.channels.length !== 2
          || !keys(doc.evidence, ["raw_block_sha256", "scope_witness_sha256"])
          || !Object.values(doc.evidence).every(sha)) invalid()
     const channels = context.channels.map((channel, index) => {
          if (!keys(channel, ["channel", "known", "generation", "status"]) || channel.channel !== index
               || typeof channel.known !== "boolean") invalid()
          const status = !channel.known ? "unknown_at_acquisition" : channel.generation === 0
               ? "no_active_stored_profile" : "declared_generation_only"
          if (channel.status !== status || (!channel.known ? channel.generation !== null : !uint32(channel.generation))) invalid()
          return Object.freeze({ channel: index, known: channel.known, generation: channel.generation, status })
     })
     return Object.freeze({ startedMs: context.started_ms, channels: Object.freeze(channels) })
}

// Copy only the already-validated source binding. No UID/account/raw bytes are
// retained for display, and this client comparison does not grant access.
const contextAnchor = (doc, mapped) => Object.freeze({
     source: Object.freeze({ record_id: String(doc.source.record_id), sample_row_id: String(doc.source.sample_row_id),
          device_id: doc.source.device_id, producer_boot_id: doc.source.producer_boot_id,
          sequence: doc.source.sequence, content_sha256: doc.source.content_sha256 }),
     rawSha: doc.evidence.raw_block_sha256, witnessSha: doc.evidence.scope_witness_sha256,
     channels: Object.freeze(mapped.channels.map(({ channel, known, generation }) => Object.freeze({ channel, known, generation }))),
})

const profileMetadata = (doc, anchor, candidates = null) => {
     const discovery = candidates === null
     if (!keys(doc, ["version", "visibility", "evidence_kind", "control_domain", "controls_hardware",
          "calibration_applied", "ec25_applied", "source", "channels", "profile_sources", "evidence"])
          || doc.version !== 1 || doc.visibility !== "private" || doc.control_domain !== "aeroponic"
          || doc.evidence_kind !== (discovery ? "original_profile_candidate_discovery" : "explicit_original_profile_byte_coherence")
          || doc.controls_hardware !== false || doc.calibration_applied !== false || doc.ec25_applied !== false
          || !keys(doc.source, Object.keys(anchor.source))
          || !Object.keys(anchor.source).every(key => doc.source[key] === anchor.source[key])
          || !decimalId(doc.source.record_id) || !decimalId(doc.source.sample_row_id)
          || !keys(doc.evidence, ["selection", "supplied_pool_is_complete_history", ...(discovery ? ["complete_physical_history"] : []),
               "raw_block_sha256", "scope_witness_sha256"])
          || doc.evidence.selection !== (discovery ? "bounded_original_admission_index_seeks" : "explicit_original_record_pks")
          || doc.evidence.supplied_pool_is_complete_history !== false || (discovery && doc.evidence.complete_physical_history !== false)
          || doc.evidence.raw_block_sha256 !== anchor.rawSha || doc.evidence.scope_witness_sha256 !== anchor.witnessSha
          || !Array.isArray(doc.channels) || doc.channels.length !== 2
          || !Array.isArray(doc.profile_sources) || doc.profile_sources.length > 2) invalid()
     const sources = doc.profile_sources.map(source => {
          if (!keys(source, ["record_id", "content_sha256", "profile_sha256"]) || !decimalId(source.record_id)
               || !sha(source.content_sha256) || !sha(source.profile_sha256)) invalid()
          return Object.freeze({ record_id: source.record_id, content_sha256: source.content_sha256, profile_sha256: source.profile_sha256 })
     })
     if (new Set(sources.map(s => s.record_id)).size !== sources.length
          || new Set(sources.map(s => s.profile_sha256)).size !== sources.length) invalid()
     const channels = doc.channels.map((item, index) => {
          const expected = anchor.channels[index]
          if (!keys(item, ["channel", "known", "generation", "status", "profile_sha256"])
               || item.channel !== expected.channel || item.known !== expected.known || item.generation !== expected.generation) invalid()
          const status = !item.known ? "unknown_at_acquisition" : item.generation === 0 ? "no_active_stored_profile" : item.status
          if (item.status !== status || (!item.known || item.generation === 0 ? item.profile_sha256 !== null
               : !["profile_bytes_unavailable", "profile_bytes_coherent"].includes(item.status)
                    || (item.status === "profile_bytes_unavailable" ? item.profile_sha256 !== null
                         : !sha(item.profile_sha256) || !sources.some(source => source.profile_sha256 === item.profile_sha256)))) invalid()
          return Object.freeze({ channel: index, known: item.known, generation: item.generation, status: item.status, profile_sha256: item.profile_sha256 })
     })
     const referenced = channels.filter(channel => channel.status === "profile_bytes_coherent").map(channel => channel.profile_sha256)
     if (new Set(referenced).size !== referenced.length || sources.length !== referenced.length
          || !sources.every(source => referenced.filter(value => value === source.profile_sha256).length === 1)
          || (candidates && (JSON.stringify(sources) !== JSON.stringify(candidates.sources)
               || JSON.stringify(channels) !== JSON.stringify(candidates.channels)))) invalid()
     return Object.freeze({ sources: Object.freeze(sources), channels: Object.freeze(channels) })
}

export default function AeroponicEcSampleContext({ state }) {
     const sample = state?.dashboard?.latest_sample
     if (state?.passiveEnabled !== true || typeof state.scopeKey !== "string" || !state.scopeKey
          || !token(state.deviceId, 3) || !id(sample?.id) || sample.device_id !== state.deviceId) return null
     return <ContextView key={JSON.stringify([state.scopeKey, state.deviceId, sample.id])}
          sampleRowId={sample.id} deviceId={state.deviceId} />
}

function ContextView({ sampleRowId, deviceId }) {
     const { language, locale } = usePortalLanguage()
     const text = TEXT[language] || TEXT.it
     const [data, setData] = useState(null)
     const [error, setError] = useState(null)
     const [loading, setLoading] = useState(false)
     const [anchor, setAnchor] = useState(null)
     const [profiles, setProfiles] = useState(null)
     const [profileError, setProfileError] = useState(false)
     const [profileLoading, setProfileLoading] = useState(false)
     const mounted = useRef(false)
     const request = useRef(null)
     const sequence = useRef(0)
     useEffect(() => {
          mounted.current = true
          const expired = () => {
               sequence.current++; request.current?.controller.abort(); request.current = null
               setData(null); setAnchor(null); setProfiles(null); setProfileError(false)
               setLoading(false); setProfileLoading(false); setError("auth")
          }
          window.addEventListener("feeder-session-expired", expired)
          return () => { mounted.current = false; request.current?.controller.abort()
               window.removeEventListener("feeder-session-expired", expired) }
     }, [])
     const load = async () => {
          if (request.current?.kind === "context") return
          request.current?.controller.abort()
          const controller = new AbortController()
          const operation = { controller, kind: "context", token: ++sequence.current }
          request.current = operation
          const current = () => mounted.current && request.current === operation && !controller.signal.aborted && sequence.current === operation.token
          setAnchor(null); setProfiles(null); setProfileError(false); setProfileLoading(false)
          setLoading(true); setData(null); setError(null)
          try {
               const doc = await loadAeroponicEcSampleContext(sampleRowId, { signal: controller.signal })
               const result = mapEcSampleContext(doc, sampleRowId, deviceId)
               const binding = contextAnchor(doc, result)
               if (current()) { setData(result); setAnchor(binding) }
          } catch (failure) {
               if (current()) setError(failure?.status === 401
                    ? "auth" : failure?.status === 403 ? "ready" : "unavailable")
          } finally {
               if (request.current === operation) { request.current = null; if (mounted.current) setLoading(false) }
          }
     }
     const matchProfiles = async () => {
          if (!anchor || request.current) return
          const controller = new AbortController()
          const operation = { controller, kind: "profiles", token: ++sequence.current }
          request.current = operation
          const current = () => mounted.current && request.current === operation && !controller.signal.aborted && sequence.current === operation.token
          setProfiles(null); setProfileError(false); setProfileLoading(true)
          try {
               const found = profileMetadata(await loadAeroponicEcProfileCandidates(sampleRowId, { signal: controller.signal }), anchor)
               if (!current()) return
               const checked = profileMetadata(await loadAeroponicEcProfileCoherence(sampleRowId,
                    found.sources.map(source => source.record_id), { signal: controller.signal }), anchor, found)
               if (current()) setProfiles(checked)
          } catch (failure) {
               if (current()) {
                    if ([401, 403].includes(failure?.status)) {
                         setData(null); setAnchor(null); setError(failure.status === 401 ? "auth" : "ready")
                    } else setProfileError(true)
               }
          } finally {
               if (request.current === operation) { request.current = null; if (mounted.current) setProfileLoading(false) }
          }
     }
     const number = (value) => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)
     return <section className="vf-leaf-shadow" aria-label={text.title} data-testid="aeroponic-ec-sample-context">
          <div className="vf-card-heading"><h4>{text.title}</h4><button className="vf-secondary-button"
               disabled={loading} onClick={load}>{data || error ? text.retry : text.load}</button></div>
          <p className="vf-trend-disclaimer">{text.note}</p>
          {loading && <p role="status">{text.loading}</p>}
          {error && <p role="alert">{text[error]}</p>}
          {data && <><div className="vf-history-stats">{data.channels.map((channel) => <div key={channel.channel}>
               <span>{channel.channel === 0 ? "EC IN" : "EC OUT"}</span>
               <strong>{text[channel.status === "unknown_at_acquisition" ? "unknown"
                    : channel.status === "no_active_stored_profile" ? "empty" : "declared"]}</strong>
               {channel.known && <small>{text.generation}: {number(channel.generation)}</small>}
          </div>)}</div><p>{text.tick}: {number(data.startedMs)} ms</p><small>{text.clock}</small></>}
          {data && anchor && <AeroponicOriginalRecordEvidence recordId={anchor.source.record_id} scopeRef={anchor} language={language} />}
          {data && anchor?.channels.some(channel => channel.known && channel.generation > 0) && <div>
               <button className="vf-secondary-button" disabled={loading || profileLoading} onClick={matchProfiles}>{text.profiles}</button>
               <p>{text.profileNote}</p>
               {profileLoading && <p role="status">{text.profileLoading}</p>}
               {profileError && <p role="alert">{text.profileUnavailable}</p>}
               {profiles && <section aria-label={text.profileTitle} data-testid="ec-profile-observations">
                    {profiles.channels.map(channel => <p key={channel.channel}>
                         {channel.channel === 0 ? "EC IN" : "EC OUT"}: {channel.status === "profile_bytes_coherent" ? text.coherent
                              : channel.status === "unknown_at_acquisition" ? text.unknown
                                   : channel.status === "no_active_stored_profile" ? text.empty : text.profileUnavailable}
                    </p>)}
               </section>}
          </div>}
     </section>
}
